import { Injectable } from '@angular/core';

/**
 * Contexto do ticket de impressão de PDF.
 *
 * A página de impressão (`/reports/print/analysis`) é aberta pelo Selenium do
 * backend **sem sessão Auth0**. As chamadas autenticadas (template, dados dos
 * widgets, insights) precisam do `accessToken` que vem no ticket — não do
 * `AuthService`. Este serviço guarda esse token (e o tenant do ticket) para que
 * os interceptors globais (`AuthTokenInterceptor`, `TenantInterceptor`) o
 * injetem quando presente.
 *
 * Fora da página de impressão o contexto fica vazio e os interceptors seguem o
 * comportamento normal (token do Auth0 / tenant por hostname).
 *
 * `providedIn: 'root'` para ser visível aos interceptors declarados no
 * `AppModule`.
 */
@Injectable({ providedIn: 'root' })
export class PdfTicketContext {
  /** Bearer do usuário, vindo do ticket. Vazio fora da página de impressão. */
  private _accessToken: string | null = null;
  /** Tenant do ticket (fallback do `X-Tenant-Id` quando o hostname não resolve). */
  private _tenantId: string | null = null;
  /**
   * Base de API a usar no lugar de `environment.apiURL` (render por origem interna,
   * ex.: `/api/1.0` com nginx local). Vem do query param `apiBase` da página de print
   * e precisa ser setada ANTES da primeira request (inclusive o fetch do ticket).
   */
  private _apiBaseOverride: string | null = null;

  /** Ativa o modo impressão com o token/tenant do ticket. Chamado uma única vez. */
  set(accessToken: string, tenantId: string | null): void {
    this._accessToken = accessToken || null;
    this._tenantId = tenantId || null;
  }

  setApiBaseOverride(apiBase: string | null): void {
    this._apiBaseOverride = apiBase || null;
  }

  clear(): void {
    this._accessToken = null;
    this._tenantId = null;
    this._apiBaseOverride = null;
  }

  get accessToken(): string | null {
    return this._accessToken;
  }

  get tenantId(): string | null {
    return this._tenantId;
  }

  get apiBaseOverride(): string | null {
    return this._apiBaseOverride;
  }

  /** true quando estamos na página de impressão (ticket já carregado). */
  get isPrintMode(): boolean {
    return this._accessToken != null;
  }
}
